import type { RatingBoard, RatingStandings } from '@brush/api-client';
import { describeApiError } from '@brush/api-client';
import { ErrorNotice, Spinner } from '@brush/ui';
import type { UseQueryResult } from '@tanstack/react-query';
import { exclusionNotice, previewEmptyNotice, previewView } from '../../ratings/board';
import { previewUnavailableHint } from '../../ratings/builder';

/**
 * §9.2's live preview, beside the eight sections.
 *
 * <b>It is half of what the builder is for.</b> An operator cannot reason about a decay ramp
 * without seeing what it does to real players: a ramp is five numbers between 0 and 1 and every
 * arrangement of them looks reasonable on the page. What tells somebody 0.5 is too steep for their
 * room is watching a regular who has not entered anything since the spring fall out of the top
 * twenty — and that is a fact about their players, not about the numbers.
 *
 * <b>It runs `/compute`, not `/standings`, and the difference is the whole of §8 on this screen.</b>
 * `/standings` serves what a recalculation froze; `/compute` runs the engine and answers what the
 * board <em>would</em> say. A builder showing the frozen figures would not move as the operator
 * edits, which is precisely the thing it exists to do — and a builder whose Save moved the
 * published figures would be the disagreement `GET /drift` exists to report rather than to cause.
 *
 * <b>It shows the SAVED configuration, and says so.</b> The engine reads the board out of the
 * database, so until the operator saves, the preview is about the board as it stands rather than as
 * it is on screen. Stating that is not a hedge: a pane silently showing stale figures beside an
 * edited ramp is worse than no pane, because it looks like the ramp did nothing.
 *
 * <b>ENTIRELY as saved, which it was not.</b> The rows came from `/compute` and the §4.6 display
 * rule applied to them came from the DRAFT, so one control in section 7 was live and the one above
 * it was not — change the minimum and nothing moved, change the display rule and rows vanished, and
 * an operator doing both read the second as the first working. `previewView` takes the rule off the
 * same response the rows came from and offers no way to pass another.
 *
 * <b>There is no control in this pane, deliberately, and there is nothing in it dressed as one.</b>
 * It carried a neutral `Pill` reading *Preview* in the header — a `<span>` status chip, but styled
 * from the same border, surface and uppercase tracking as `smallButtonClass`, whose disabled state is
 * that styling at 40% opacity. So it read as a permanently greyed-out PREVIEW button, in a panel
 * header, next to a caption telling the operator to act — and the reasonable reading was that the
 * product was refusing an instruction it had just given. What the pane is, is said in words in the
 * footer instead.
 *
 * <b>What is NOT built is previewing an unsaved draft</b> — see known gap 13. §9.2 asks for a live
 * preview "as the points system builder does", and that builder posts its unsaved definition inline;
 * `POST /{id}/compute` takes an id and an anchor and scores what is in the database. Until that is
 * closed, the footer's *Save to see a change here* is the honest description of the product rather
 * than a hedge, and it must not be softened into implying otherwise.
 */
export function RatingPreviewPane({
  board,
  standings,
  asOf,
  draftPointsSystemId,
}: {
  board: RatingBoard;
  standings: UseQueryResult<RatingStandings>;
  asOf: string;

  /**
   * What section 2 names on screen, which is not necessarily what the server scored. Only for the
   * refusal hint: a chosen-but-unsaved system is why `/compute` says it has none, and the pane used
   * to answer that by telling the operator to choose one.
   */
  draftPointsSystemId: string | null;
}) {
  const live = standings.data ?? null;

  // The rows and the rule that hides some of them, from the one computation — see `previewView`.
  const view = previewView(live);

  const exclusions = live ? exclusionNotice(live) : null;

  const unavailable = previewUnavailableHint({
    stored: board.pointsSystemId,
    draft: draftPointsSystemId,
  });

  const empty = previewEmptyNotice(view);

  return (
    <aside className="min-w-0 lg:sticky lg:top-6 lg:self-start">
      <div className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
        <header className="border-b border-ink-800/10 px-4 py-3">
          {/* No chip here. A neutral `Pill` reading "Preview" is a <span>, but it wears
              `smallButtonClass`'s costume — same border, same surface, same uppercase tracking — and
              that class's disabled state is exactly that styling faded. In a panel header it read as
              a dead PREVIEW button. What this pane is, the footer says in words. */}
          <h2 className="font-display text-sm font-semibold text-ink-950">What this rates</h2>
          {/* §2.2's anchor. A rolling-window rating moves with the calendar on its own, so a figure
              with no anchor cannot answer "why did my rating drop overnight". */}
          <p className="num mt-0.5 text-[10.5px] text-ink-800/50">as of {asOf}</p>
        </header>

        {standings.isLoading ? (
          <div className="px-4 py-8">
            <Spinner label="Scoring…" />
          </div>
        ) : standings.isError ? (
          <div className="px-4 py-4">
            <ErrorNotice message={describeApiError(standings.error)} />
            {/* Read from the draft as well as the board: a chosen-but-unsaved system is why the
                server says it has none, and naming section 2 there sends an operator back to the
                one step they have already taken. See `previewUnavailableHint`. */}
            {unavailable ? (
              <p className="mt-2 text-[11.5px] leading-relaxed text-ink-800/60">{unavailable}</p>
            ) : null}
          </div>
        ) : view === null || empty !== null ? (
          /*
           * TWO empty panes, named apart. One sentence covered both and had the advice backwards
           * for each: the minimum cannot empty this pane (§3.6 — qualification governs presentation
           * and never computation), and where it IS the fix, "nobody is rated by this yet" is false
           * about a board with 348 rated players. See `previewEmptyNotice`.
           */
          <p className="px-4 py-8 text-center text-[11.5px] leading-relaxed text-ink-800/50">
            {(empty ?? NOTHING_COMPUTED).lead}
            <br />
            <span className="text-[11px]">{(empty ?? NOTHING_COMPUTED).detail}</span>
          </p>
        ) : (
          <>
            <ol className="divide-y divide-ink-800/5">
              {view.rows.slice(0, PREVIEW_ROWS).map((row) => (
                <li key={row.playerId} className="flex items-baseline gap-3 px-4 py-2">
                  {/* The rank is the computation's and is never renumbered — ties share one and
                      the next one skips (1, 2, 2, 4), so a row index would be wrong here before
                      any filter was applied. */}
                  <span className="num w-6 shrink-0 text-right text-[11px] text-ink-800/45">
                    {row.rank}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink-950">
                    {row.playerName}
                    {!row.isQualified ? (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gold-500">
                        provisional
                      </span>
                    ) : null}
                  </span>
                  <span className="num shrink-0 text-[12.5px] font-semibold text-ink-950">
                    {row.rating.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </li>
              ))}
            </ol>

            <div className="space-y-1 border-t border-ink-800/10 px-4 py-2.5 text-[11px] leading-relaxed text-ink-800/55">
              {/* A capped list with no total reads as the whole board. Same rule the Overview's
                  glance sections keep. */}
              <p>
                Showing {Math.min(view.rows.length, PREVIEW_ROWS)} of{' '}
                <strong className="num text-ink-950">{view.total}</strong> rated
                {view.hiddenByQualification > 0
                  ? `, with ${view.hiddenByQualification} below the minimum held back`
                  : ''}
                .
              </p>
              {/* §4.3 and §9.5: the exclusions are two different facts and a silent one is how a
                  player disappears with nobody able to say why. */}
              {exclusions ? <p>{exclusions}</p> : null}
            </div>
          </>
        )}

        <p className="border-t border-ink-800/10 bg-ink-800/[0.02] px-4 py-2.5 text-[11px] leading-relaxed text-ink-800/55">
          Computed from this rating <strong>as saved</strong>. Save to see a change here — and this
          is a preview either way: it publishes nothing, and the standings a player reads only move
          when somebody recalculates.
        </p>
      </div>
    </aside>
  );
}

/** Enough to see the shape of a ramp change without turning the pane into the standings screen. */
const PREVIEW_ROWS = 12;

/**
 * Settled, no data, no error — which the branches above have already ruled out and which this
 * query, always enabled, should not reach. Here so that if it ever does the pane says something
 * true rather than throwing, and `previewEmptyNotice` stays a rule about a computation that
 * happened rather than growing a third state it cannot describe.
 */
const NOTHING_COMPUTED = {
  lead: 'Nothing has been computed for this rating yet.',
  detail: 'Reload the screen. If it stays empty, the preview is not reaching the server.',
};
